import {
  Accessor,
  createEffect,
  createMemo,
  createSignal,
  For,
  JSX,
  Match,
  Show,
  Switch,
} from "solid-js";
import { RnrRouteData } from "~/server/data/login_rnr_route_data";
import { AxisHeader } from "../header/header_axis";
import { Spacer } from "~/widgets/spacer";
import { createJob } from "~/utils/job";
import { ModalInnerFrame, useModal } from "../modal";
import { PhosphorIcon } from "~/widgets/icons";
import { clientRepo } from "~/server/apis/client_repo";
import { setCookie } from "~/utils/client_cookie";
import { Cookie } from "~/types";
import { useIsRouting, useNavigate } from "@solidjs/router";
import { setIsFreshLogin } from "../header/header_webstore";
import { config } from "~/data/config";
import { clientIdConfig } from "~/data/partner_config";
import { LoginPageFooter$Client } from "~/components/partner_components/login_page_footer";
import { BottomStickyBranding$Client } from "~/components/partner_components/partner_bottom_page_branding";
import {
  CollectIdentifer,
  CollectOtp,
  ErrorState,
  identifyInput,
  LoadingState,
  SubmitButton,
} from "./login_form";
import { APIError } from "~/utils/fetch";
import { RnrPortalsHeader } from "../header/header_rnr_portals";
import { ButtonRegular } from "~/widgets/button";
import { VerifyCodeResponse } from "~/server/types/brand";
import { createInputHandler } from "~/utils/input";

export default function LoginRnrCodeComponent(props: {
  routeData: Accessor<RnrRouteData | undefined>;
}) {
  const [isFieldActive, setIsFieldActive] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | undefined>("");
  const [code, setCode] = createSignal<string>("");
  const [emailOrPhoneNumber, setEmailOrPhoneNumber] = createSignal("");
  const [isMaskedPhoneNumberAvailable, setIsMaskedPhoneNumberAvailable] =
    createSignal(false);
  const [otp, setOTP] = createSignal<string | null>(null);
  const [otpToken, setOTPToken] = createSignal<string | null>(null);
  const [resetCountdown, setResetCountdown] = createSignal(30);
  let timer: NodeJS.Timeout;

  const [errorState, setErrorState] = createSignal<ErrorState>({});
  const [loading, setLoading] = createSignal<LoadingState>({
    generateOTP: false,
    verifyOTP: false,
    verifyCode: false,
  });

  const [isDropdownOpen, setIsDropdownOpen] = createSignal<boolean>(false);
  const { openModal, closeModal, setIsModalOn, updateModalContentGetter } =
    useModal()!;

  const navigate = useNavigate();
  let inputRef: HTMLInputElement | undefined;

  const isRouting = useIsRouting();

  let phoneInput: HTMLInputElement | undefined;
  createEffect(() => {
    phoneInput?.focus();
  });

  const resendOtp = () => {
    setOTP(null);
    const inputType = identifyInput((emailOrPhoneNumber() ?? "").trim());
    if (inputType) {
      generateOTP(inputType);
    }
  };

  const generateOTP = async (
    inputType: "email" | "phoneNumber"
  ): Promise<boolean> => {
    setLoading((prevLoadingState) => ({
      ...prevLoadingState,
      generateOTP: true,
    }));
    setErrorState({});
    try {
      const payload = {
        clientId: props.routeData()?.clientId ?? "",
        clientSecret: props.routeData()?.clientSecret ?? "",
      };
      const body: any = {
        code: code(),
      };
      if (!isMaskedPhoneNumberAvailable()) {
        body[inputType] = emailOrPhoneNumber();
      }
      const response = await clientRepo.generatePartnerCodeOTP(payload, body, {
        isUnauthenticated: true,
      });

      if (response.otpToken) {
        setOTPToken(response.otpToken);
        startCountdown();
        setLoading((prevLoadingState) => ({
          ...prevLoadingState,
          generateOTP: false,
        }));
        return true;
      }
      return false;
    } catch (error) {
      if (error instanceof APIError) {
        setErrorState({
          emailOrPhoneNumber: error.message,
        });
      } else {
        setErrorState({
          emailOrPhoneNumber: "Something went wrong. Please try again later.",
        });
      }
      resetModalValues();
      return false;
    } finally {
      setLoading({
        generateOTP: false,
        verifyOTP: false,
        verifyCode: false,
      });
    }
  };

  function resetModalValues() {
    setEmailOrPhoneNumber("");
    setOTP(null);
    setOTPToken(null);
  }

  const handleSubmit: JSX.EventHandler<HTMLFormElement, Event> = (event) => {
    event.preventDefault();
    if (
      otpToken() !== null &&
      (otp() ?? "").length === 6 &&
      !!emailOrPhoneNumber()
    ) {
      verifyOTP();
      return;
    }

    const inputType = identifyInput((emailOrPhoneNumber() ?? "").trim());
    if (inputType) {
      generateOTP(inputType);
    } else {
      setErrorState({
        emailOrPhoneNumber:
          "Please enter a valid email or a 10-digit phone number.",
      });
    }
  };

  const verifyOTP = async () => {
    setLoading((prevLoadingState) => ({
      ...prevLoadingState,
      verifyOTP: true,
    }));
    const inputType = identifyInput((emailOrPhoneNumber() ?? "").trim());
    try {
      const verifyOTPResponse = await clientRepo.verifyPartnerCodeOTP(
        {
          clientId: props.routeData()?.clientId ?? "",
          clientSecret: props.routeData()?.clientSecret ?? "",
        },
        {
          [inputType!]: emailOrPhoneNumber(),
          otp: otp() ?? "",
          otpToken: otpToken() ?? "",
          code: code(),
        },
        {
          isUnauthenticated: true,
          throwAuthError: true,
        }
      );

      if (verifyOTPResponse.sessionId) {
        setCookie({
          key: Cookie.SessionId,
          value: verifyOTPResponse.sessionId,
          expiryInMinutes: config.sesssionDurationInMinutes.rnr,
        });
        setIsFreshLogin(true);
        closeModal();
        navigate("/");
      } else {
        setErrorState({
          otp: "An unexpected error occurred. Please try again later.",
        });
        resetTimer();
        resetModalValues();
      }
    } catch (error: any) {
      if (error instanceof APIError && error.code === 401) {
        setErrorState({ otp: "Invalid OTP. Please try again. " });
      } else {
        setErrorState({
          otp: error?.message,
        });
      }
      resetTimer();
      resetModalValues();
    } finally {
      setOTP("");
      setLoading({
        generateOTP: false,
        verifyOTP: false,
        verifyCode: false,
      });
    }
  };

  const startCountdown = () => {
    setResetCountdown(30);
    timer = setInterval(() => {
      setResetCountdown((prev) => {
        if (prev === 1) {
          clearInterval(timer);
          return prev - 1;
        }
        return prev - 1;
      });
    }, 1000);
  };

  createEffect(async () => {
    if (
      otpToken() !== null &&
      (otp() ?? "").length === 6 &&
      !!emailOrPhoneNumber()
    ) {
      await verifyOTP();
    }
  });

  const resetTimer = () => {
    clearInterval(timer);
    setResetCountdown(0);
  };

  const submitJob = createJob<VerifyCodeResponse>({
    errorCallback: (error) => {
      setErrorMessage(error.message);
    },
    initialJob: async () => {
      const verifyCodeResponse = await clientRepo.verifyPartnerCode(
        {
          clientId: props.routeData()?.clientId ?? "",
          clientSecret: props.routeData()?.clientSecret ?? "",
        },
        {
          code: code(),
        }
      );

      if (verifyCodeResponse.sessionId) {
        setCookie({
          key: Cookie.SessionId,
          value: verifyCodeResponse.sessionId,
          expiryInDays: config.sesssionDurationInMinutes.axis_portal,
        });
        setIsFreshLogin(true);
        navigate("/");
        return verifyCodeResponse;
      } else if (verifyCodeResponse.sessionId === null) {
        resetModalValues();
        setErrorState({});
        setIsMaskedPhoneNumberAvailable(!!verifyCodeResponse.maskedPhoneNumber);
        let showOtpScreen = !!verifyCodeResponse.maskedPhoneNumber;
        if (showOtpScreen) {
          setEmailOrPhoneNumber(verifyCodeResponse.maskedPhoneNumber || "");
          const otpGenerated = await generateOTP("phoneNumber");
          if (!otpGenerated) {
            showOtpScreen = false;
          }
        }
        openModal(() => {
          return (
            <div class="w-full rounded-2xl border border-[#212427] bg-black p-6 md:max-w-[480px]">
              <form class="w-full md:min-w-[412px]" onSubmit={handleSubmit}>
                <Show
                  when={!showOtpScreen && otpToken() === null && otp() === null}
                  fallback={
                    <CollectOtp
                      emailOrPhoneNumber={emailOrPhoneNumber}
                      setErrorState={setErrorState}
                      mode={() => "rnr"}
                      errorState={errorState}
                      loading={loading}
                      otp={otp}
                      resendOtp={resendOtp}
                      resetCountdown={resetCountdown}
                      setOTP={setOTP}
                      isModal={true}
                      setOTPToken={setOTPToken}
                      setResetCountdown={setResetCountdown}
                    />
                  }
                >
                  <div class="flex justify-between pb-4">
                    <p class="text-h3">Enter your phone no.</p>
                    <CloseModalButton />
                  </div>
                  <CollectIdentifer
                    mode={() => "rnr"}
                    authMethod={() => "PHONE"}
                    emailOrPhoneNumber={emailOrPhoneNumber}
                    setEmailOrPhoneNumber={setEmailOrPhoneNumber}
                    errorState={errorState}
                    setErrorState={setErrorState}
                    inputRef={inputRef}
                    isModal={true}
                    placeholderText={""}
                  />
                </Show>
                <div class="mt-6">
                  <SubmitButton
                    mode={() => "rnr"}
                    authMethod={() => "PHONE"}
                    loading={loading}
                  />
                </div>
              </form>
            </div>
          );
        }, "md:!w-[480px]");
        return verifyCodeResponse;
      } else {
        throw new Error(
          "An unexpected error occurred. Please try again later."
        );
      }
    },
  });

  const getClientId = createMemo(() => {
    const data = props.routeData();
    if (data?.mode === "rnr") {
      return data?.clientId;
    }
  });

  return (
    <>
      <div class="relative z-10 flex h-full flex-col items-center justify-start overflow-hidden pb-[80px]">
        <Switch>
          <Match when={getClientId() === clientIdConfig.axisClientId}>
            <AxisHeader
              isLoggedIn={false}
              mode={props.routeData()?.mode ?? "rnr"}
              isDropdownOpen={isDropdownOpen}
              clientId={props.routeData()?.clientId}
              setIsDropdownOpen={setIsDropdownOpen}
              rnrPortalsJson={props.routeData()?.rnrPortalsJson}
              coins={0}
            />
          </Match>
          <Match when={props.routeData()?.rnrPortalsJson}>
            <RnrPortalsHeader
              rnrPortalsJson={props.routeData()?.rnrPortalsJson}
              isLoggedIn={() => false}
              mode={props.routeData()?.mode ?? "rnr"}
              isDropdownOpen={isDropdownOpen}
              setIsDropdownOpen={setIsDropdownOpen}
              clientLogoUrl={
                props.routeData()?.clientLogoUrl ??
                "https://assets.myhubble.money/web-assets/tata_digital_logo_new.svg"
              }
              clientId={props.routeData()?.clientId}
            />
          </Match>
        </Switch>
        <div class="mt-[128px] sm:mt-[144px]"></div>
        <span class="text-center text-normal text-textNormal">
          <Switch>
            <Match
              when={getClientId() === clientIdConfig.godrejPropertiesClientId}
            >
              <span class="md:hidden">
                Congratulations on your <br /> new home at Godrej Properties! 🏡
              </span>
              <span class="hidden md:block">
                Congratulations on your new home at Godrej Properties! 🏡
              </span>
            </Match>
            <Match
              when={
                (props.routeData()?.rnrPortalsJson?.getLoginPageIntroText
                  ?.length ?? 0) > 0
              }
            >
              {props.routeData()?.rnrPortalsJson?.getLoginPageIntroText}
            </Match>
          </Switch>
        </span>
        <div class="mt-3 sm:mt-4"></div>

        <span
          class="max-w-[351px] text-center font-jakartaSans text-h2 font-medium sm:max-w-[700px]"
          classList={{
            "sm:text-[46px]":
              getClientId() === clientIdConfig.godrejPropertiesClientId,
            "sm:text-[52px]":
              getClientId() !== clientIdConfig.godrejPropertiesClientId,
          }}
        >
          <Switch>
            <Match
              when={
                getClientId() === clientIdConfig.axisClientId ||
                getClientId() === clientIdConfig.moneycontrolClientId ||
                getClientId() === clientIdConfig.bajajClientId
              }
            >
              We’re grateful to <br /> have you as a customer
            </Match>
            <Match when={getClientId() === clientIdConfig.tdClientId}>
              <span class="hidden md:block">
                Avail exciting offers <br />
                on gift cards with NeuCard
              </span>
              <span class="md:hidden">
                Avail exciting <br />
                offers on gift cards <br /> with NeuCard
              </span>
            </Match>
            <Match
              when={
                (props.routeData()?.rnrPortalsJson?.getLoginPageIntroHeader
                  .length ?? 0) > 0
              }
            >
              {props.routeData()?.rnrPortalsJson?.getLoginPageIntroHeader}
            </Match>
          </Switch>
        </span>

        <div class="mt-12 sm:mt-14"></div>
        {/* Don't show branding when keyboard is out in the mobile as it can sometimes intersect with other elements. */}
        <LoginForm onInputFocus={() => { }} onInputBlur={() => { }} />
        <div class="mt-[29px] sm:mt-6"></div>
        <Show
          when={
            props.routeData()?.rnrPortalsJson?.showHowItWorksForCodeBasedPortal
          }
        >
          <span
            class="z-10 cursor-pointer text-subtitleSmall text-textNormal underline-offset-2"
            onClick={() => {
              updateModalContentGetter(() => (
                <HowItWorks
                  howItWorks={
                    getClientId() === clientIdConfig.axisClientId
                      ? axisHowItWorks
                      : undefined
                  }
                />
              ));
              setIsModalOn(true);
            }}
          >
            <u>How it works?</u>
          </span>
        </Show>
        <div class="fixed bottom-0 left-0 right-0">
          <LoginPageFooter$Client
            clientId={getClientId}
            rnrPortalsJson={props.routeData()?.rnrPortalsJson}
          />
          <BottomStickyBranding$Client clientId={getClientId} />
        </div>
      </div>
    </>
  );

  function getCodeEntryText(clientId?: string): JSX.Element {
    if (clientId === clientIdConfig.axisClientId) {
      return "ENTER CODE TO REDEEM YOUR VOUCHER";
    } else if (clientId === clientIdConfig.tdClientId) {
      return (
        <span class="text-center text-medium">
          Enter coupon code received in the communication <br /> to avail the
          offer
        </span>
      );
    }
    return "ENTER CODE TO UNLOCK REWARDS";
  }

  function LoginForm(props: {
    onInputFocus: () => void;
    onInputBlur: () => void;
  }) {
    return (
      <div class="z-10 w-full px-4 sm:w-min sm:px-0">
        <div class="flex w-full flex-col items-center justify-center rounded-2xl border border-[#333232] bg-baseLight p-4 sm:w-[412px] sm:p-6">
          <span class="text-center text-f12Bold  text-textNormal">
            {getCodeEntryText(getClientId())}
          </span>
          <Spacer height={20} />
          <InputField onFocus={props.onInputFocus} onBlur={props.onInputBlur} />
          <Spacer height={20} />
          <ButtonDarkV2
            isLoading={submitJob.jobState() === "running" || isRouting()}
            onClick={() => {
              if (code().length < 1) {
                setErrorMessage("Please enter code to continue");
                return;
              } else if (
                code().length <
                (getClientId() === clientIdConfig.tdClientId ? 18 : 16)
              ) {
                setErrorMessage("Please enter a valid code");
                return;
              } else if (submitJob.jobState() == "running") return;
              submitJob.run();
            }}
          >
            Redeem
          </ButtonDarkV2>
        </div>
        {/* <BorderGradient>

        </BorderGradient> */}
      </div>
    );
  }

  function InputField(props: { onFocus: () => void; onBlur: () => void }) {
    return (
      <div class="flex w-full flex-col">
        <div
          class="w-full rounded-xl border bg-baseTertiaryLight px-4 py-[13px] transition-colors darkTheme:bg-baseTertiaryDark"
          classList={{
            "border-basePrimaryDark": isFieldActive(),
            "border-baseTertiaryDark": !isFieldActive(),
            "border-errorDark": !!errorMessage(),
          }}
        >
          <form
            class="text flex text-bold text-textDark"
            onSubmit={(e) => {
              e.preventDefault();
              submitJob.run();
            }}
          >
            <input
              id="code-input-rnr-code-component"
              class="flex-1 bg-transparent text-[16px] text-textDark placeholder:text-normal placeholder:text-textNormal"
              ref={phoneInput}
              placeholder={`${getClientId() === clientIdConfig.tdClientId ? "18" : "16"} digit code`}
              value={code()}
              onInput={createInputHandler(setCode)}
              maxlength={30}
              onFocus={() => {
                setIsFieldActive(true);
                props.onFocus();
              }}
              onBlur={() => {
                setIsFieldActive(false);
                props.onBlur();
              }}
            />
          </form>
        </div>
        <Show when={errorMessage()}>
          <Spacer height={4} />
          <span class="text-medium text-errorDark">{errorMessage()}</span>
        </Show>
      </div>
    );
  }
}

const axisHowItWorks = [
  "Enter the code & click Redeem",
  "Select the brand and click on claim your gift card",
  "You will receive the gift card details instantly",
];

export function HowItWorks(props: { howItWorks?: string[] }) {
  const howHubbleCodesWork = [
    "Enter the code & click “Redeem”",
    "You’ll receive coins in wallet. 1 coin = ₹1",
    "Get the gift card of your choice using coins",
  ];

  return (
    <ModalInnerFrame>
      <div class="flex w-full flex-row items-start justify-between">
        <span class="text-nowrap text-h4 text-basePrimaryDark">
          How it works?
        </span>
        <CloseModalButton />
      </div>
      <div class="mt-5"></div>
      <ol class="flex flex-col gap-2">
        <For each={props.howItWorks ?? howHubbleCodesWork}>
          {(text, index) => (
            <li class="items flex flex-row items-center justify-start gap-2 text-basePrimaryDark darkTheme:text-baseSecondaryDark">
              <div class="flex h-[18px] w-[18px] items-center justify-center rounded-full border bg-baseTertiaryLight text-[10px] font-bold darkTheme:border-none darkTheme:bg-baseSecondaryLight">
                {index() + 1}
              </div>{" "}
              <span class="text-[15px] font-normal">{text}</span>
            </li>
          )}
        </For>
      </ol>
    </ModalInnerFrame>
  );
}

export function CloseModalButton(props: { onClick?: () => void }) {
  const { setIsModalOn, updateModalContentGetter } = useModal()!;

  return (
    <div
      id="close-modal-button"
      onClick={(event) => {
        event.stopPropagation();
        if (props.onClick) {
          props.onClick();
          return;
        }
        setIsModalOn(false);
        updateModalContentGetter(() => <></>);
      }}
    >
      <div class="flex h-8 w-8 cursor-pointer items-center justify-center rounded-[50%] border bg-baseTertiaryLight darkTheme:border-[#555] darkTheme:bg-[#222427] dark:border-basePrimaryLight dark:bg-basePrimaryDark">
        <PhosphorIcon
          name="x"
          size="bold"
          fontSize={16}
          class="text-basePrimaryDark dark:text-baseTertiaryLight"
        />
      </div>
    </div>
  );
}

export function ButtonDarkV2(props: {
  isLoading: boolean;
  onClick: () => void;
  children: JSX.Element;
}) {
  return (
    <ButtonRegular
      onClick={props.onClick}
      isEnabled={true}
      class="button-filled w-full"
      height="48px"
      isLoading={props.isLoading}
    >
      {props.children}
    </ButtonRegular>
  );
}
